import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Link } from 'react-router-dom';
import Home from './Home';
import Statut from './Statut';
import ZdolnySlazak from './ZdolnySlazak';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDark, setIsDark] = useState(() => {
        const saved = localStorage.getItem('theme');
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        return saved ? saved === 'dark' : prefersDark;
    });
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const rootEl = document.documentElement;
        rootEl.classList.toggle('dark', isDark);
        localStorage.setItem('theme', isDark ? 'dark' : 'light');
    }, [isDark]);

    const handleNavigation = (id) => {
        if (location.pathname !== '/') {
            navigate('/', { state: { scrollTo: id } });
        } else {
            scrollToSection(id);
        }
        setIsOpen(false);
    };

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const offset = 80; // Navbar height
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            window.scrollTo({
                top: offsetPosition,
                behavior: "smooth"
            });
        }
    };

    useEffect(() => {
        if (location.state && location.state.scrollTo) {
            const timer = setTimeout(() => {
                scrollToSection(location.state.scrollTo);
                // Clear state to avoid scrolling on subsequent renders
                window.history.replaceState({}, document.title);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [location]);

    return (
        <nav className="fixed top-0 left-0 w-full z-20 dark:text-gray-100 transition-all duration-300">
            <div className="bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200/50 dark:border-gray-800/50">
                <div className="container mx-auto px-4 py-4 flex items-center justify-between">
                    <button onClick={() => handleNavigation('hero')} className="flex items-center space-x-2">
                        <img src={isDark ? '/logo-dark.svg' : '/logo.svg'} alt="Fundacja - logo" className="h-8 w-16" />
                        <span className="text-lg md:text-xl font-extrabold text-blue-600 bg-white dark:bg-gray-900">Fundacja Absolwentów II UWr</span>
                    </button>
                    <div className="hidden md:block">
                        <ul className="flex items-center space-x-6 text-sm font-medium text-gray-700 dark:text-gray-100 list-none">
                            <li><button onClick={() => handleNavigation('about')} className="hover:text-blue-700 transition-colors">O nas</button></li>
                            <li><button onClick={() => handleNavigation('programs')} className="hover:text-blue-700 transition-colors">Programy</button></li>
                            <li><button onClick={() => handleNavigation('contact')} className="hover:text-blue-700 transition-colors">Kontakt</button></li>
                            <li><Link to="/statut" className="hover:text-blue-700 transition-colors">Statut</Link></li>
                            <li><Link to="/zdolny-slazak" className="hover:text-blue-700 transition-colors">zDolny Ślązak</Link></li>
                            <li>
                                <button onClick={() => handleNavigation('cta')} className="ml-2 inline-flex items-center rounded-full bg-blue-600 px-5 py-2.5 text-white shadow-md shadow-blue-600/20 hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/30 hover:-translate-y-0.5 transition-all duration-300 font-medium">Wesprzyj</button>
                            </li>
                        </ul>
                    </div>
                    <ThemeToggle isDark={isDark} setIsDark={setIsDark} />
                    <button onClick={() => setIsOpen(!isOpen)} className="md:hidden inline-flex items-center justify-center rounded-md p-2 text-gray-700 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none">
                        <span className="sr-only">Otwórz menu</span>
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
                        </svg>
                    </button>
                </div>
                {isOpen && (
                    <div className="md:hidden border-t border-gray-200">
                        <ul className="px-4 py-3 space-y-2 text-gray-700 dark:text-gray-100 list-none">
                            <li><button onClick={() => handleNavigation('about')} className="w-full text-left py-2 hover:text-blue-700">O nas</button></li>
                            <li><button onClick={() => handleNavigation('programs')} className="w-full text-left py-2 hover:text-blue-700">Programy</button></li>
                            <li><Link to="/zdolny-slazak" onClick={() => setIsOpen(false)} className="block w-full text-left py-2 hover:text-blue-700">zDolny Ślązak</Link></li>
                            <li><Link to="/statut" onClick={() => setIsOpen(false)} className="block w-full text-left py-2 hover:text-blue-700">Statut</Link></li>
                            <li><button onClick={() => handleNavigation('contact')} className="w-full text-left py-2 hover:text-blue-700">Kontakt</button></li>
                            <li><button onClick={() => handleNavigation('cta')} className="w-full text-left mt-2 inline-flex items-center rounded-full bg-blue-700 px-4 py-2 text-white shadow-sm hover:bg-blue-800">Wpłać</button></li>
                        </ul>
                    </div>
                )}
            </div>
        </nav>
    );
};

const ThemeToggle = ({ isDark, setIsDark }) => {
    return (
        <button
            onClick={() => setIsDark(!isDark)}
            className="mr-2 hidden md:inline-flex items-center justify-center rounded-full p-2 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-100"
            aria-label="Przełącz motyw"
            title="Przełącz motyw"
        >
            {isDark ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
            ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2m0 14v2m9-9h-2M5 12H3m15.364 6.364l-1.414-1.414M7.05 7.05 5.636 5.636m12.728 0-1.414 1.414M7.05 16.95 5.636 18.364M12 8a4 4 0 100 8 4 4 0 000-8z" />
                </svg>
            )}
        </button>
    );
};

const Footer = () => {
    const navigate = useNavigate();

    const handleNavigation = (id) => {
        navigate('/', { state: { scrollTo: id } });
    };

    return (
        <footer className="bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800">
            <div className="container mx-auto px-4 py-12">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-sm text-gray-600 dark:text-gray-300">
                    <div>
                        <h4 className="text-gray-900 dark:text-white font-semibold mb-3">Fundacja</h4>
                        <ul className="space-y-2">
                            <li><button onClick={() => handleNavigation('about')} className="hover:text-blue-700">O nas</button></li>
                            <li><button onClick={() => handleNavigation('programs')} className="hover:text-blue-700">Cele</button></li>
                            <li><Link to="/statut" className="hover:text-blue-700">Statut</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-gray-900 dark:text-white font-semibold mb-3">Wsparcie</h4>
                        <ul className="space-y-2">
                            <li><button onClick={() => handleNavigation('cta')} className="hover:text-blue-700">Jak pomóc</button></li>
                            <li><button onClick={() => handleNavigation('cta')} className="hover:text-blue-700">Darowizny</button></li>
                            <li><button onClick={() => handleNavigation('contact')} className="hover:text-blue-700">Partnerzy</button></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-gray-900 dark:text-white font-semibold mb-3">Kontakt</h4>
                        <ul className="space-y-2">
                            <li>ul. Joliot-Curie 15</li>
                            <li>50-523 Wrocław</li>
                            <li><a className="text-blue-700 hover:underline" href="mailto:fundacjaabsolwentowiiuwr@cs.uni.wroc.pl">fundacjaabsolwentowiiuwr@cs.uni.wroc.pl</a></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="text-gray-900 dark:text-white font-semibold mb-3">Śledź nas</h4>
                        <div className="flex gap-3">
                            <a href="https://www.instagram.com/fundacja.ii.uwr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 h-10 w-10 text-gray-700 dark:text-gray-200 transition-colors duration-200 hover:bg-gradient-to-tr hover:from-pink-500 hover:via-purple-500 hover:to-yellow-500 hover:text-white dark:hover:text-white" aria-label="Instagram" title="Instagram">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
                                    <rect x="3" y="3" width="18" height="18" rx="5"></rect>
                                    <circle cx="12" cy="12" r="4"></circle>
                                    <circle cx="17.5" cy="6.5" r="1.5"></circle>
                                </svg>
                            </a>
                            <a href="https://www.facebook.com/profile.php?id=61558673312467" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 h-10 w-10 text-gray-700 dark:text-gray-200 transition-colors duration-200 hover:bg-[#1877F2] hover:text-white dark:hover:text-white" aria-label="Facebook" title="Facebook">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                                    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06C2 17.06 5.66 21.2 10.44 22v-7.03H7.9v-2.9h2.54V9.8c0-2.5 1.48-3.88 3.75-3.88 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34V22C18.34 21.2 22 17.06 22 12.06z" />
                                </svg>
                            </a>
                            <a href="https://www.tiktok.com/@fundacja.ii.uwr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 h-10 w-10 text-gray-700 dark:text-gray-200 transition-colors duration-200 hover:bg-gradient-to-tr hover:from-[#25F4EE] hover:to-[#FE2C55] hover:text-white dark:hover:text-white" aria-label="TikTok" title="TikTok">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6">
                                    <path d="M12 3v5.2a6.8 6.8 0 004.8 2.1v2.5a9.3 9.3 0 01-4.8-1.6v4.4a4.4 4.4 0 11-4-4.37V13a1.9 1.9 0 00-1.2-.05 6.1 6.1 0 00-.8.27 6.9 6.9 0 00-.3 2 4.4 4.4 0 008.8 0V3h-2.5z" />
                                </svg>
                            </a>
                            <a href="https://x.com/fundacja_ii_uwr" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 h-10 w-10 text-gray-700 dark:text-gray-200 transition-colors duration-200 hover:bg-black hover:text-white dark:hover:text-white" aria-label="X" title="X (Twitter)">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6">
                                    <path d="M4 4l16 16M20 4L4 20" />
                                </svg>
                            </a>
                        </div>
                    </div>
                </div>
                <div className="mt-8 border-t border-gray-200 dark:border-gray-800 pt-6 text-center text-xs text-gray-500 dark:text-gray-400">© {new Date().getFullYear()} Fundacja Absolwentów Informatyki UWr</div>
            </div>
        </footer>
    );
};

function App() {
    return (
        <Router>
            <div className="bg-gray-100 dark:bg-gray-900 font-sans min-h-screen flex flex-col">
                <Navbar />
                <div className="flex-grow">
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/statut" element={<Statut />} />
                        <Route path="/zdolny-slazak" element={<ZdolnySlazak />} />
                    </Routes>
                </div>
                <Footer />
            </div>
        </Router>
    );
}

export default App;
